import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { directory, tasks } from '@cwos/api-client';
import {
  FilteredEmpty,
  Pill,
  Refused,
  ScopeNote,
  SuccessEmpty,
  TableCard,
  type Column,
} from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { personDirectory } from '../personDirectory.js';
import { PersonChip } from '../PersonChip.js';
import { CompletionStatement } from './CompletionStatement.js';
import {
  ORIGIN_LABEL,
  PRIORITY_LABEL,
  PRIORITY_VARIANT,
  STATUS_LABEL,
  STATUS_VARIANT,
  dueSentence,
  modeLabel,
  taskTitle,
} from './taskLogic.js';

/**
 * The canonical Tasks queue — the first surface from which a human can see the
 * obligations this platform is tracking.
 *
 * The domain landed with a complete contract, seven routes and no screen, which
 * is the state this repository keeps calling out: a capability nothing can call
 * is a capability nobody has.
 *
 * ---------------------------------------------------------------------------
 * THE COLUMN THAT MATTERS IS "COMPLETION", NOT "STATUS"
 * ---------------------------------------------------------------------------
 * A status column alone is how the three completion modes get flattened. "Marked
 * done" beside a mode column saying "hybrid" leaves the reader to reconcile the
 * two, and readers reconcile generously. So every row renders
 * `<CompletionStatement>`, which says whose claim it is and whether Phibian has
 * verified anything — and the word "verified" appears as an affirmation on
 * exactly the rows where Phibian actually observed the condition resolve.
 *
 * ---------------------------------------------------------------------------
 * PEOPLE
 * ---------------------------------------------------------------------------
 * A task carries `app_user.id`s and nothing else about the people in it. Names
 * and photos are resolved against `directory.workers()` through
 * `personDirectory` — the photo contract's rule that a domain never grows an
 * avatar field of its own. `GET /api/tasks` already requires `directory:read`
 * for exactly this reason (a task names the person responsible for it, so
 * reading one is reading the workforce), so the second read is not a new
 * disclosure; it is the one the task route already assumed.
 */

const PAGE_SIZE = 50;

/**
 * The read key the routes actually guard on.
 *
 * `task:read`, and it is not a rename. This constant read `alerts:read` for as
 * long as the routes borrowed that key, and it went on reading `alerts:read`
 * after 1758090000000 seeded the real keys and `tasks/routes.ts:107` bound
 * `TASK_READ = 'task:read'` to every task read — so the client was gating three
 * surfaces on a key the server had stopped checking.
 *
 * BOTH DIRECTIONS OF THAT ARE WRONG, and both are constructible: `task:read`
 * and `alerts:read` are independently `grantable` (permissionMetadata.ts), so an
 * administrator can hand out either without the other.
 *
 *   - A principal holding `task:read` + `directory:read` and NOT `alerts:read`
 *     is admitted by every task route and was refused the rail entry, `/tasks`
 *     and `/tasks/:id` by this constant. Refused though entitled.
 *   - A principal holding `alerts:read` + `directory:read` and NOT `task:read`
 *     was offered the rail entry and rendered the screen, and every request it
 *     makes is a 403. Admitted though refused — the exact defect the rail entry
 *     in `navigation.ts` warns about in its own comment.
 *
 * The three shipped roles that hold `alerts:read` (org_admin, supervisor,
 * worker) are the same three that hold `task:read`, which is why nothing broke
 * on a stock tenant and why this is worth a comment rather than a bug report:
 * the gate was measurably wrong and coincidentally quiet.
 *
 * NO NEW KEY. `task:read` already promises "your own work"; inventing a
 * `tasks-screen:read` would make a worker seeing their own queue an
 * administrator's checkbox. `KNOWN_PERMISSION` and
 * `ORG_ADMIN_GRANTABLE_PERMISSION` are untouched by this change.
 *
 * `directory:read` is the route's SECOND gate and is deliberately not folded in
 * here: the rail's `permission` field is one key by construction, and the screen
 * already renders `Refused` naming both keys when the read comes back 403.
 */
export const TASK_READ_KEY = 'task:read';

type StatusFilter = 'all' | 'outstanding' | tasks.TaskStatus;

/**
 * The default view: obligations that are not discharged.
 *
 * `tasks.isOpenStatus` is the DOMAIN's answer — the four lifecycle statuses in
 * which the task is still moving — and it deliberately excludes
 * `completion_claimed`, because a claim is a resting place in the state
 * machine.
 *
 * It is the wrong default for THIS screen, and the difference is the product's
 * whole argument. A task somebody marked done that Phibian has not verified is
 * exactly the task an administrator needs to see: it is the only state where
 * the platform knows less than the queue implies. Filing it under "not
 * outstanding" would hide the unverified claims behind a dropdown, which is a
 * quieter version of rendering them as complete.
 *
 * So the default is "still owed", and it is a superset of `isOpenStatus` by one
 * status, named here rather than by re-implementing the domain's list.
 */
function stillOwed(status: tasks.TaskStatus): boolean {
  return tasks.isOpenStatus(status) || status === 'completion_claimed';
}

export function TasksQueue() {
  const navigate = useNav();
  const permissions = usePermissions();
  const [status, setStatus] = useState<StatusFilter>('outstanding');
  const [mode, setMode] = useState<'all' | tasks.VerificationMode>('all');

  /**
   * THE SCOPE-AWARE QUESTION, AND ONLY FROM A RESOLVED SET.
   *
   * Two defects in one expression. `has('directory:read') && has('user:read')`
   * is question 1 asked twice, and `GET /directory/workers` charges question 2:
   * both of its gates are `can()` at ORGANIZATION scope, so a program-scoped
   * holder of either key satisfies `has` and is then refused by the route. The
   * query fired, 403'd, and `personDirectory` read `isError` — which renders
   * "Name unavailable", the sentence for a directory that FAILED. A refusal
   * reported as a glitch, on every row of the queue.
   *
   * `mayListWorkers` is the single client-side statement of that rule, which is
   * why this is a call to it and not a third copy of the conjunction.
   *
   * And it is false while the set is loading and after it fails, so the
   * `<ScopeNote>` below — which tells the reader their account cannot read the
   * directory — is claimed only once the set has resolved.
   */
  const canReadDirectory = mayListWorkers(permissions);
  const directoryRefused = !permissions.isLoading && !permissions.isError && !canReadDirectory;

  const list = useQuery({
    queryKey: ['tasks', 'list', status, mode],
    queryFn: () =>
      tasks.list({
        // `outstanding` is a CLIENT concept: the API filters by one status, and
        // the four open statuses are four separate reads. Rather than fire four,
        // the unfiltered page is read and narrowed here — and the filter summary
        // says so, so nobody reads this list as "every open task in the org"
        // when it is the first page of them.
        status: status === 'all' || status === 'outstanding' ? undefined : status,
        verificationMode: mode === 'all' ? undefined : mode,
        limit: PAGE_SIZE,
      }),
  });

  const workers = useQuery({
    queryKey: ['directory', 'workers', 'for-tasks'],
    queryFn: () => directory.workers(),
    enabled: canReadDirectory,
  });

  const people = personDirectory(
    { data: workers.data, isLoading: workers.isLoading, isError: workers.isError },
    canReadDirectory,
  );

  const page = list.data;
  const rows = (page?.tasks ?? []).filter((task) =>
    status === 'outstanding' ? stillOwed(task.status) : true,
  );
  const now = new Date();

  const columns: Column<tasks.TaskView>[] = [
    {
      key: 'title',
      header: 'Obligation',
      render: (task) => (
        <span className="rn-col">
          <span className="rn-strong">{taskTitle(task)}</span>
          <span className="rn-muted">{ORIGIN_LABEL[task.origin]}</span>
        </span>
      ),
    },
    {
      key: 'responsible',
      header: 'Responsible',
      render: (task) => (
        <PersonChip
          person={people.person(task.responsibleUserId ?? task.assigneeUserId)}
          detail={tasks.ROUTING_TARGET_LABEL[task.assigneeKind]}
        />
      ),
    },
    {
      key: 'due',
      header: 'Due',
      render: (task) => <span>{dueSentence(task, now)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (task) => (
        <span className="rn-col">
          <Pill variant={STATUS_VARIANT[task.status]}>{STATUS_LABEL[task.status]}</Pill>
          <Pill variant={PRIORITY_VARIANT[task.priority]}>{PRIORITY_LABEL[task.priority]}</Pill>
        </span>
      ),
    },
    {
      key: 'completion',
      header: 'What is known',
      // The whole reason the domain exists, rendered on every row.
      render: (task) => <CompletionStatement task={task} people={people} />,
    },
    {
      key: 'mode',
      header: 'How it completes',
      render: (task) => <span className="rn-muted">{modeLabel(task.verificationMode)}</span>,
    },
  ];

  const filtered = status !== 'outstanding' || mode !== 'all';

  return (
    <div className="rn-col">
      <header className="rn-col">
        <h1>Tasks</h1>
        <p className="rn-muted">
          Obligations this platform is tracking. A task exists until it is closed, its history
          cannot be edited, and how it completes was decided when it was created and cannot change.
        </p>
      </header>

      {directoryRefused ? (
        <ScopeNote scope="tasks without the names of the people responsible for them — your account cannot read the workforce directory" />
      ) : null}

      <div className="rn-row" role="group" aria-label="Filter tasks">
        <label>
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
            <option value="outstanding">Still owed (open, or claimed but not verified)</option>
            <option value="all">Every status</option>
            <option value="completion_claimed">Marked done, not verified</option>
            <option value="verified">Verified by Phibian</option>
            {/*
              THE "ESCALATED" OPTION IS BACK, and the reason it was gone is why
              it may return. It was withheld because `escalated` was RESERVED
              AND NOT PERFORMED — nothing in the platform wrote an escalation,
              so the filter could only ever come back empty, and an empty list
              under a filter a customer chose reads as "you have no escalated
              tasks" rather than "this platform does not escalate". Offering a
              value is a claim that the platform produces it.

              The platform produces it now: `tasks/service.runTaskEscalations`,
              on the `system.task-escalations` queue, raises an overdue task
              under its own `task_escalation_policy`.
              `RESERVED_UNPERFORMED_TASK_STATUSES` in `@cwos/api-client` is
              empty, and `tasks-status-filter-honesty.test.tsx` fails if this
              option disagrees with it in EITHER direction.
            */}
            <option value="escalated">Escalated</option>
            <option value="closed">Closed</option>
          </select>
        </label>
        <label>
          How it completes
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as 'all' | tasks.VerificationMode)}
          >
            <option value="all">Any mode</option>
            <option value="system_verified">Phibian verifies</option>
            <option value="human_attested">Person attests</option>
            <option value="hybrid">Person attests, Phibian watches</option>
          </select>
        </label>
      </div>

      <ListState
        phase={listPhase(list, page === undefined ? undefined : rows.length)}
        surface="table"
        rows={6}
        failedMessage="We couldn’t load tasks just now. This is not a statement that nothing is owed."
        onRetry={() => void list.refetch()}
        refused={
          <Refused
            heading="Tasks are not available on this account"
            message="Reading a task means reading the name of the person responsible for it, so it needs both the task read permission and permission to view the workforce directory."
          />
        }
        empty={
          filtered ? (
            <FilteredEmpty
              onClear={() => {
                setStatus('outstanding');
                setMode('all');
              }}
            />
          ) : (
            <SuccessEmpty
              heading="Nothing outstanding"
              message="No task is open, and nothing is resting on a claim Phibian has not verified. Phibian raises a task when an obligation appears; nobody has to remember to."
            />
          )
        }
      >
        <TableCard
          columns={columns}
          rows={rows}
          rowKey={(task) => task.id}
          onRowOpen={(task) => navigate(`/tasks/${task.id}`)}
          caption={`${rows.length} task${rows.length === 1 ? '' : 's'}${
            page && page.total > rows.length ? ` of ${page.total}` : ''
          }`}
        />
      </ListState>
    </div>
  );
}
